import React, { useState } from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Target, Clock, CheckCircle, BarChart3, Award, Building } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EBITDABusinessCase = () => {
  const [loading, setLoading] = useState(false);
  const [inputData, setInputData] = useState({
    strategy_name: 'QRM',
    business_type: 'Metal/Steel Manufacturing',  // Business type selector
    company_size_fte: '51-200',          // Company size for smart advice
    current_annual_revenue: 5000000,      // $5M default
    current_ebitda: 500000,              // $500K default (10% margin)
    current_ebitda_margin: 10,           // 10% margin
    target_ebitda_improvement_percent: 25, // 25% improvement target
    // Detailed Investment Breakdown
    investment_training_consultants: 75000,  // $75K training/consultants
    investment_extra_capacity: 50000,       // $50K extra capacity  
    investment_software: 15000,             // $15K software
    investment_hardware: 10000,             // $10K hardware
    investment_timeframe_months: 12,
    // MCT-Specific Parameters (from Alpha Training)
    current_mct_days: 30,                // 30 days current MCT
    target_mct_reduction_percent: 50,    // 50% MCT reduction target
    current_wip_units: 1000,             // 1000 units WIP
    target_wip_reduction_percent: 40,    // 40% WIP reduction
    current_defect_rate_percent: 5,      // 5% defect rate
    target_defect_reduction_percent: 60, // 60% defect reduction
    has_clear_product_families: true,    // VSM suitability
    is_high_mix_low_volume: true,        // QRM suitability
    annual_revenue_growth_percent: 20    // Revenue growth from competitive advantage
  });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setInputData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear previous results when parameters change - user needs to recalculate
    setResults(null);
    setError(null);
    
    // Auto-calculate EBITDA margin when revenue or EBITDA changes
    if (field === 'current_annual_revenue' || field === 'current_ebitda') {
      const newData = { ...inputData, [field]: value };
      const margin = (newData.current_ebitda / newData.current_annual_revenue) * 100;
      setInputData(prev => ({
        ...prev,
        [field]: value,
        current_ebitda_margin: margin
      }));
    }
  };

  const handleSliderChange = (field, value) => {
    setInputData(prev => ({
      ...prev,
      [field]: value[0]
    }));
    
    // Clear previous results when parameters change - user needs to recalculate
    setResults(null);
    setError(null);
  };

  const calculateBusinessCase = async () => {
    setLoading(true);
    setError(null);
    setResults(null);  // 🔄 CLEAR PREVIOUS RESULTS immediately when user clicks calculate
    
    try {
      const userId = localStorage.getItem('userId') || 'demo_user';
      
      const response = await axios.post(`${API}/ebitda-roi-analysis`, {
        ...inputData,
        user_id: userId
      });
      
      setResults(response.data);
      console.log('✅ New calculation completed:', response.data);
    } catch (error) {
      console.error('Failed to calculate business case:', error);
      
      // Provide specific error guidance based on response
      if (error.response && error.response.data && error.response.data.detail) {
        const errorDetail = error.response.data.detail;
        
        // Parse validation errors and provide specific guidance
        if (errorDetail.includes('Input validation failed:')) {
          setError(`❌ Input Validation Error: ${errorDetail.replace('Input validation failed: ', '')}`);
        } else if (errorDetail.includes('Annual Revenue')) {
          setError('❌ Check Current Annual Revenue: Must be a positive number greater than 0');
        } else if (errorDetail.includes('EBITDA')) {
          setError('❌ Check Current EBITDA: Must be positive and less than Annual Revenue');
        } else if (errorDetail.includes('Investment')) {
          setError('❌ Check Investment Amounts: Training + Capacity + Software + Hardware must total more than 0');
        } else if (errorDetail.includes('MCT')) {
          setError('❌ Check MCT Parameters: Current MCT must be > 0 days, Target reduction between 1-99%');
        } else if (errorDetail.includes('WIP')) {
          setError('❌ Check WIP Parameters: Current WIP must be > 0 units, Target reduction between 0-99%');
        } else {
          setError(`❌ Calculation Error: ${errorDetail}`);
        }
      } else {
        // Network or other errors
        setError('❌ Connection Error: Unable to reach calculation server. Please try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  const strategies = [
    { value: 'QRM', label: 'Quick Responsive Manufacturing' },
    { value: 'QRM for Food Processing', label: 'QRM for Food Processing (High-Mix/Low-Volume)' },
    { value: 'QRM for Agriculture Supply Chain', label: 'QRM for Agriculture Supply Chain' },
    { value: 'Lean Manufacturing', label: 'Lean Manufacturing' },
    { value: 'Lean + QRM Hybrid (Food)', label: 'Lean + QRM Hybrid (Food Industry)' },
    { value: 'Theory of Constraints', label: 'Theory of Constraints' },
    { value: 'Kaizen', label: 'Kaizen' }, 
    { value: '5S', label: '5S' },
    { value: 'Digital Work', label: 'Digital Work' },
    { value: 'Agile Supply Chain (Food)', label: 'Agile Supply Chain (Food/Agriculture)' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen py-8 sm:py-12 bg-gray-50">
      <PageSEO path="/business-case" />
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-yellow-600 font-medium mb-2">Enterprise Financial Engine</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>QRM EBITDA Business Case Calculator</h1>
            <p className="text-base sm:text-lg text-gray-600 mb-2">
              Answer the critical question: <strong className="text-gray-900">"Will investing in {inputData.strategy_name} improve our EBITDA?"</strong>
            </p>
            <p className="text-sm text-gray-500">
              Get a 3-year financial analysis to justify your investment to shareholders and plant managers
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Input Section */}
            <div className="space-y-6 sm:space-y-8">
              <Card className="p-6 sm:p-8 bg-white border-gray-200 shadow-sm rounded-lg">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Building className="mr-3 h-5 sm:h-6 w-5 sm:w-6 text-yellow-500" />
                  Current Financial Position
                </h2>

                <div className="space-y-6">
                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Strategy to Evaluate</Label>
                    <Select 
                      value={inputData.strategy_name}
                      onValueChange={(value) => handleInputChange('strategy_name', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {strategies.map((strategy) => (
                          <SelectItem key={strategy.value} value={strategy.value}>
                            {strategy.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-2 block">🏢 Business Type</Label>
                    <Select 
                      value={inputData.business_type}
                      onValueChange={(value) => handleInputChange('business_type', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Manufacturing */}
                        <SelectItem value="Metal/Steel Manufacturing">🏭 Metal/Steel Manufacturing</SelectItem>
                        <SelectItem value="Electronics/Electrical Components">🏭 Electronics/Electrical Components</SelectItem>
                        <SelectItem value="Machinery/Equipment Manufacturing">🏭 Machinery/Equipment Manufacturing</SelectItem>
                        <SelectItem value="Job Shop/Custom Manufacturing">🏭 Job Shop/Custom Manufacturing</SelectItem>
                        <SelectItem value="Medical Device Manufacturing">🏭 Medical Device Manufacturing</SelectItem>
                        <SelectItem value="Aerospace/Defense Manufacturing">🏭 Aerospace/Defense Manufacturing</SelectItem>
                        
                        {/* Agriculture & Food */}
                        <SelectItem value="Food Processing - High-Mix/Specialty">🌱 Food Processing - High-Mix/Specialty</SelectItem>
                        <SelectItem value="Food Processing - Mass Production">🌱 Food Processing - Mass Production</SelectItem>
                        <SelectItem value="Fresh Food Distribution/Logistics">🌱 Fresh Food Distribution/Logistics</SelectItem>
                        <SelectItem value="Agricultural Equipment/Supply Chain">🌱 Agricultural Equipment/Supply Chain</SelectItem>
                        <SelectItem value="Fruit/Vegetable Growing & Packing">🌱 Fruit/Vegetable Growing & Packing</SelectItem>
                        <SelectItem value="Dairy/Meat Processing">🌱 Dairy/Meat Processing</SelectItem>
                        
                        {/* Services & Other */}
                        <SelectItem value="Engineering/Design Services">🏢 Engineering/Design Services</SelectItem>
                        <SelectItem value="Healthcare Operations">🏢 Healthcare Operations</SelectItem>
                        <SelectItem value="Logistics/Transportation Services">🏢 Logistics/Transportation Services</SelectItem>
                        <SelectItem value="Software Development">🏢 Software Development</SelectItem>
                        
                        {/* Specialized */}
                        <SelectItem value="Chemical/Pharmaceutical">⚡ Chemical/Pharmaceutical</SelectItem>
                        <SelectItem value="Automotive Supply Chain">⚡ Automotive Supply Chain</SelectItem>
                        <SelectItem value="Construction/Project-Based">⚡ Construction/Project-Based</SelectItem>
                        <SelectItem value="Textile/Fashion">⚡ Textile/Fashion</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-blue-600 mt-2">
                      <strong>Smart Analysis:</strong> Get sector-specific QRM recommendations and realistic projections
                    </p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-2 block">👥 Company Size (FTE)</Label>
                    <Select 
                      value={inputData.company_size_fte}
                      onValueChange={(value) => handleInputChange('company_size_fte', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10 employees (Micro)</SelectItem>
                        <SelectItem value="11-50">11-50 employees (Small)</SelectItem>
                        <SelectItem value="51-200">51-200 employees (Medium)</SelectItem>
                        <SelectItem value="201-500">201-500 employees (Large)</SelectItem>
                        <SelectItem value="500+">500+ employees (Enterprise)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-green-600 mt-2">
                      <strong>Implementation Guidance:</strong> Tailored QRM approach based on organizational complexity
                    </p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Current Annual Revenue</Label>
                    <Input
                      type="number"
                      value={inputData.current_annual_revenue}
                      onChange={(e) => handleInputChange('current_annual_revenue', parseFloat(e.target.value) || 0)}
                      placeholder="5000000"
                      className="text-lg"
                    />
                    <p className="text-sm text-gray-500 mt-1">Enter your company's annual revenue</p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Current EBITDA</Label>
                    <Input
                      type="number"
                      value={inputData.current_ebitda}
                      onChange={(e) => handleInputChange('current_ebitda', parseFloat(e.target.value) || 0)}
                      placeholder="500000"
                      className="text-lg"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Current EBITDA Margin: <strong>{inputData.current_ebitda_margin.toFixed(1)}%</strong>
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 sm:p-8 bg-white border-gray-200 shadow-sm rounded-lg">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Target className="mr-3 h-5 sm:h-6 w-5 sm:w-6 text-yellow-500" />
                  Investment & Targets
                </h2>

                <div className="space-y-6">
                  <div>
                    <Label className="text-lg font-semibold mb-4 block">
                      EBITDA Improvement Target: {inputData.target_ebitda_improvement_percent}%
                    </Label>
                    <Slider
                      value={[inputData.target_ebitda_improvement_percent]}
                      onValueChange={(value) => handleSliderChange('target_ebitda_improvement_percent', value)}
                      max={100}
                      min={5}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>5%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-4 block">💰 Investment Breakdown</Label>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Training & Consultants</Label>
                        <Input
                          type="number"
                          value={inputData.investment_training_consultants}
                          onChange={(e) => handleInputChange('investment_training_consultants', parseFloat(e.target.value) || 0)}
                          placeholder="75000"
                          className="text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">QRM training, consultants, change management</p>
                      </div>
                      
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Extra Capacity</Label>
                        <Input
                          type="number"
                          value={inputData.investment_extra_capacity}
                          onChange={(e) => handleInputChange('investment_extra_capacity', parseFloat(e.target.value) || 0)}
                          placeholder="50000"
                          className="text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Additional workforce, overtime, capacity expansion</p>
                      </div>
                      
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Software</Label>
                        <Input
                          type="number"
                          value={inputData.investment_software}
                          onChange={(e) => handleInputChange('investment_software', parseFloat(e.target.value) || 0)}
                          placeholder="15000"
                          className="text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">MES, POLCA systems, planning software</p>
                      </div>
                      
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Hardware</Label>
                        <Input
                          type="number"
                          value={inputData.investment_hardware}
                          onChange={(e) => handleInputChange('investment_hardware', parseFloat(e.target.value) || 0)}
                          placeholder="10000"
                          className="text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Equipment, machinery, physical improvements</p>
                      </div>
                      
                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                          <span className="font-bold text-gray-900">Total Investment:</span>
                          <span className="font-bold text-blue-600 text-xl">
                            ${(inputData.investment_training_consultants + inputData.investment_extra_capacity + inputData.investment_software + inputData.investment_hardware).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Current Manufacturing Critical-path Time (MCT)</Label>
                    <Input
                      type="number"
                      value={inputData.current_mct_days}
                      onChange={(e) => handleInputChange('current_mct_days', parseFloat(e.target.value) || 0)}
                      placeholder="30"
                      className="text-lg"
                    />
                    <p className="text-sm text-gray-500 mt-1">Time from order to delivery of first part (days)</p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-4 block">
                      🎯 MCT Reduction Target: {inputData.target_mct_reduction_percent}%
                    </Label>
                    <Slider
                      value={[inputData.target_mct_reduction_percent]}
                      onValueChange={(value) => handleSliderChange('target_mct_reduction_percent', value)}
                      max={80}
                      min={10}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>10%</span>
                      <span>45%</span>
                      <span>80%</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-2">
                      <strong>QRM Focus:</strong> Reducing Manufacturing Critical-path Time is key to competitive advantage
                    </p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Current Work-in-Progress (WIP) Units</Label>
                    <Input
                      type="number"
                      value={inputData.current_wip_units}
                      onChange={(e) => handleInputChange('current_wip_units', parseFloat(e.target.value) || 0)}
                      placeholder="1000"
                      className="text-lg"
                    />
                    <p className="text-sm text-gray-500 mt-1">Average units in production process</p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-4 block">
                      WIP Reduction Target: {inputData.target_wip_reduction_percent}%
                    </Label>
                    <Slider
                      value={[inputData.target_wip_reduction_percent]}
                      onValueChange={(value) => handleSliderChange('target_wip_reduction_percent', value)}
                      max={70}
                      min={10}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>10%</span>
                      <span>40%</span>
                      <span>70%</span>
                    </div>
                    <p className="text-sm text-green-600 mt-2">
                      <strong>Little's Law:</strong> Lower WIP with same MCT = Higher throughput
                    </p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-2 block">Current Defect Rate (%)</Label>
                    <Input
                      type="number"
                      value={inputData.current_defect_rate_percent}
                      onChange={(e) => handleInputChange('current_defect_rate_percent', parseFloat(e.target.value) || 0)}
                      placeholder="5"
                      className="text-lg"
                      min="0"
                      max="50"
                      step="0.5"
                    />
                    <p className="text-sm text-gray-500 mt-1">Percentage of products requiring rework</p>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-4 block">
                      Defect Reduction Target: {inputData.target_defect_reduction_percent}%
                    </Label>
                    <Slider
                      value={[inputData.target_defect_reduction_percent]}
                      onValueChange={(value) => handleSliderChange('target_defect_reduction_percent', value)}
                      max={90}
                      min={20}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>20%</span>
                      <span>55%</span>
                      <span>90%</span>
                    </div>
                  </div>

                  {/* VSM Suitability Assessment */}
                  <div className="border-t pt-6">
                    <Label className="text-lg font-semibold mb-4 block">📊 VSM Suitability Assessment</Label>
                    <p className="text-sm text-gray-600 mb-4">Based on QRM Alpha Training methodology</p>
                    
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="clear-product-families"
                          checked={inputData.has_clear_product_families}
                          onCheckedChange={(checked) => handleInputChange('has_clear_product_families', checked)}
                        />
                        <Label htmlFor="clear-product-families" className="text-sm">
                          Clear Product Families (Required for VSM effectiveness)
                        </Label>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="high-mix-low-volume"
                          checked={inputData.is_high_mix_low_volume}
                          onCheckedChange={(checked) => handleInputChange('is_high_mix_low_volume', checked)}
                        />
                        <Label htmlFor="high-mix-low-volume" className="text-sm">
                          High-Mix/Low-Volume Environment (Ideal for QRM)
                        </Label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-lg font-semibold mb-4 block">
                      🎯 Expected Annual Revenue Growth: {inputData.annual_revenue_growth_percent}%
                    </Label>
                    <Slider
                      value={[inputData.annual_revenue_growth_percent]}
                      onValueChange={(value) => handleSliderChange('annual_revenue_growth_percent', value)}
                      max={50}
                      min={0}
                      step={2}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Competitive Advantage:</strong> Revenue growth from faster MCT and improved reliability. 
                      Belgian manufacturers have achieved significant growth through QRM implementation.
                    </p>
                  </div>

                  <Button 
                    onClick={calculateBusinessCase}
                    size="lg"
                    disabled={loading}
                    className="w-full py-5 sm:py-6 text-base sm:text-lg font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded"
                  >
                    {loading ? (
                      <>
                        <div className="spinner mr-3"></div>
                        Calculating Business Case...
                      </>
                    ) : (
                      <>
                        <BarChart3 className="mr-3 h-6 w-6" />
                        Calculate 3-Year Business Case
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              {results && (
                <>
                  {/* Business Case Summary */}
                  <Card className={`p-6 sm:p-8 rounded-lg ${results.is_investment_justified 
                    ? 'bg-white border-2 border-emerald-400 shadow-md' 
                    : 'bg-white border-2 border-yellow-400 shadow-md'
                  }`}>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      {results.is_investment_justified ? (
                        <CheckCircle className="mr-3 h-6 w-6 text-emerald-500" />
                      ) : (
                        <Clock className="mr-3 h-6 w-6 text-yellow-500" />
                      )}
                      {results.is_investment_justified ? 'Investment Recommended' : 'Review Required'}
                    </h2>
                    
                    <div className="p-4 sm:p-6 bg-gray-50 rounded-lg mb-6 border border-gray-200">
                      <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                        {results.business_case_summary}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                        <div className="text-3xl font-bold text-green-600 mb-2">
                          {results.roi_percent.toFixed(1)}%
                        </div>
                        <div className="text-gray-600">3-Year ROI</div>
                      </div>
                      
                      <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                        <div className="text-3xl font-bold text-blue-600 mb-2">
                          {results.payback_period_months.toFixed(1)}
                        </div>
                        <div className="text-gray-600">Payback (Months)</div>
                      </div>
                    </div>
                  </Card>

                  {/* MCT Analysis - QRM Alpha Training Focus */}
                  <Card className="p-8 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                      <Clock className="mr-3 h-6 w-6 text-indigo-600" />
                      Manufacturing Critical-path Time (MCT) Analysis
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-700">Current MCT:</span>
                          <span className="font-bold text-gray-900">{results.current_mct_days} days</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-700">Target MCT:</span>
                          <span className="font-bold text-green-600">{results.target_mct_days?.toFixed(1)} days</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-700">MCT Reduction:</span>
                          <span className="font-bold text-blue-600">{results.mct_reduction_percent?.toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-700">Current WIP:</span>
                          <span className="font-bold text-gray-900">{results.current_wip_units} units</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-700">Target WIP:</span>
                          <span className="font-bold text-green-600">{results.target_wip_units?.toFixed(0)} units</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-700">Throughput Improvement:</span>
                          <span className="font-bold text-purple-600">{results.throughput_improvement_percent?.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Little's Law Display */}
                    <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-200">
                      <h4 className="font-bold text-indigo-800 mb-3">📐 Little's Law Application (Authentic QRM Formula)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Current Throughput:</span>
                          <span className="font-bold text-gray-900 ml-2">{results.current_throughput_units_day?.toFixed(1)} units/day</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Improved Throughput:</span>
                          <span className="font-bold text-green-600 ml-2">{results.improved_throughput_units_day?.toFixed(1)} units/day</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Touch Time Analysis:</span>
                          <span className="font-bold text-blue-600 ml-2">Value-added focus</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Non-Touch Time:</span>
                          <span className="font-bold text-red-600 ml-2">Primary reduction target</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 italic">
                        Authentic QRM Formula: Throughput = WIP ÷ MCT | Critical Path Analysis from MCT Tool
                      </p>
                    </div>
                    
                    {/* Power of Six Integration */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                      <h4 className="font-bold text-purple-800 mb-3">🏆 Power of Six Integration (Tubino's Advanced Formula)</h4>
                      <div className="text-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mathematical Model:</span>
                          <span className="font-mono text-purple-700">(M/M₀) = (L/L₀)^0.17</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lead Time Ratio:</span>
                          <span className="font-bold text-purple-600">{(results.target_mct_days / results.current_mct_days).toFixed(3)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Cost Improvement:</span>
                          <span className="font-bold text-green-600">Enhanced by Power of Six</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 italic">
                        Advanced QRM Gold: (Cost_after/Cost_before)^6 = Lead_time_after/Lead_before
                      </p>
                    </div>
                    
                    {/* VSM Suitability Score */}
                    <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
                      <h4 className="font-bold text-blue-800 mb-2">📊 VSM Suitability Score</h4>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Strategy Suitability:</span>
                        <div className="flex items-center">
                          <span className="font-bold text-blue-600 text-lg mr-2">{results.vsm_suitability_score?.toFixed(1)}/10</span>
                          <Badge className={`${results.vsm_suitability_score >= 7 ? 'bg-green-100 text-green-800' : 
                                              results.vsm_suitability_score >= 5 ? 'bg-yellow-100 text-yellow-800' : 
                                              'bg-red-100 text-red-800'}`}>
                            {results.vsm_suitability_score >= 7 ? 'Highly Suitable' : 
                             results.vsm_suitability_score >= 5 ? 'Suitable' : 'Consider Alternatives'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Financial Impact */}
                  <Card className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">3-Year Financial Impact</h3>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                        <span className="font-medium text-gray-700">Year 1 EBITDA Impact</span>
                        <span className="font-bold text-blue-600">{formatCurrency(results.year1_ebitda_impact)}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                        <span className="font-medium text-gray-700">Year 2 EBITDA Impact</span>
                        <span className="font-bold text-green-600">{formatCurrency(results.year2_ebitda_impact)}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                        <span className="font-medium text-gray-700">Year 3 EBITDA Impact</span>
                        <span className="font-bold text-purple-600">{formatCurrency(results.year3_ebitda_impact)}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
                        <span className="font-bold text-gray-900">Total 3-Year Benefit</span>
                        <span className="font-bold text-gray-900 text-xl">{formatCurrency(results.cumulative_3yr_benefit)}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg">
                        <span className="font-medium text-gray-700">Investment Required</span>
                        <span className="font-bold text-red-600">-{formatCurrency(results.investment_amount)}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-emerald-100 rounded-lg border-2 border-emerald-300">
                        <span className="font-bold text-gray-900">Net 3-Year Benefit</span>
                        <span className={`font-bold text-xl ${results.net_3yr_benefit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(results.net_3yr_benefit)}
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* EBITDA Progression */}
                  <Card className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">EBITDA Margin Progression</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span>Current EBITDA Margin:</span>
                        <span className="font-bold">{results.current_ebitda_margin.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Year 3 Projected Margin:</span>
                        <span className="font-bold text-green-600">
                          {((results.current_ebitda + results.year3_ebitda_impact) / results.current_annual_revenue * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Margin Improvement:</span>
                        <span className="font-bold text-blue-600">
                          +{(((results.current_ebitda + results.year3_ebitda_impact) / results.current_annual_revenue * 100) - results.current_ebitda_margin).toFixed(1)} percentage points
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* Professional Disclaimer */}
                  <Card className="p-8 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      ⚠️ Important Disclaimer
                    </h3>
                    <div className="text-gray-700 space-y-3">
                      <p className="text-sm leading-relaxed">
                        <strong>Results depend entirely on focus and commitment of the whole team.</strong> These projections assume successful implementation with strong leadership support, consistent execution, and sustained organizational commitment to the chosen strategy.
                      </p>
                      <p className="text-sm leading-relaxed">
                        Actual results may vary based on market conditions, implementation quality, team engagement, and unforeseen circumstances. Past performance does not guarantee future results.
                      </p>
                      <p className="text-sm leading-relaxed">
                        <strong>Success factors include:</strong> Leadership commitment, employee training, cultural change management, consistent measurement, and continuous improvement focus throughout the implementation period.
                      </p>
                      
                      {/* Evidence-Based Transparency */}
                      <div className="border-t pt-3 mt-4">
                        <p className="text-sm leading-relaxed">
                          <strong>Sector-Specific Evidence Base:</strong> Recommendations are based on validated case studies where available. 
                          <strong>High confidence:</strong> Manufacturing (Belgian manufacturers, NIKO case studies), Agricultural Equipment (John Deere), Food Processing (Packo Inox). 
                          <strong>Moderate confidence:</strong> Fresh food distribution, logistics sectors (based on QRM principles and time-criticality). 
                          <strong>Limited evidence:</strong> Healthcare, software, construction (estimates based on QRM suitability theory, limited case studies).
                        </p>
                      </div>
                      
                      <div className="border-t pt-3 mt-3">
                        <p className="text-sm leading-relaxed">
                          <strong>Company Size Considerations:</strong> Implementation approaches and timeframes are based on QRM training principles (Two Pizza Rule, cell formation requirements) and organizational change management theory. Larger organizations typically require more complex change management and longer implementation periods.
                        </p>
                      </div>
                      
                      <div className="bg-blue-50 p-3 rounded-lg mt-3">
                        <p className="text-xs text-blue-800 leading-relaxed">
                          <strong>Methodology:</strong> This calculator integrates authentic QRM methodology from Rajan Suri's "It's About Time", 
                          QRM Alpha/Silver/Gold training materials, Power of Six mathematical models, and validated case studies. 
                          Sector recommendations combine proven QRM principles with industry-specific analysis where direct QRM case studies are limited.
                        </p>
                      </div>
                    </div>
                  </Card>
                </>
              )}

              {error && (
                <Card className="p-6 sm:p-8 bg-white border-2 border-red-300 rounded-lg shadow-sm">
                  <div className="text-red-500 text-center">
                    <h3 className="text-lg sm:text-xl font-bold mb-2">Calculation Error</h3>
                    <p className="text-gray-600">{error}</p>
                  </div>
                </Card>
              )}

              {!results && !error && !loading && (
                <Card className="p-6 sm:p-8 text-center bg-white border-gray-200 shadow-sm rounded-lg">
                  <div className="text-gray-300 mb-4">
                    <BarChart3 className="h-14 sm:h-16 w-14 sm:w-16 mx-auto" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-medium text-gray-700 mb-2">Ready to Calculate</h3>
                  <p className="text-gray-500 text-sm">
                    Adjust your parameters and click <strong className="text-yellow-600">"Calculate 3-Year Business Case"</strong> to see if investing in <strong className="text-gray-900">{inputData.strategy_name}</strong> will improve your EBITDA.
                  </p>
                  <p className="text-sm text-yellow-600 mt-3">
                    Change any parameter above to see different results
                  </p>
                </Card>
              )}
              
              {loading && (
                <Card className="p-6 sm:p-8 text-center bg-white border-gray-200 shadow-sm rounded-lg">
                  <div className="text-yellow-500 mb-4">
                    <div className="spinner mx-auto mb-4" style={{width: '48px', height: '48px'}}></div>
                  </div>
                  <h3 className="text-lg sm:text-xl font-medium text-yellow-600 mb-2">Calculating New Results...</h3>
                  <p className="text-gray-500 text-sm">
                    Running financial analysis for <strong className="text-gray-900">{inputData.strategy_name}</strong> with your updated parameters...
                  </p>
                  <p className="text-sm text-gray-400 mt-3">
                    This may take a few seconds
                  </p>
                </Card>
              )}
            </div>
          </div>

          {/* Enhanced Case Study Reference with Implementation Costs */}
          {results && (
            <Card className="mt-8 sm:mt-12 p-6 sm:p-8 bg-white border-gray-200 shadow-sm rounded-lg">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Award className="mr-2 h-5 w-5 text-yellow-500" />
                Real-World Validation: Leading Manufacturer Implementation Journey
              </h3>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">
                Jan Pauwels (Managing Director & Smart QRM Navigator Founder) achieved spectacular results through systematic QRM implementation:
              </p>
              <div className="grid grid-cols-3 gap-4 sm:gap-6 text-center mb-6">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl sm:text-2xl font-bold text-yellow-500">69%</div>
                  <div className="text-xs sm:text-sm text-gray-500">Lead Time Reduction</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl sm:text-2xl font-bold text-yellow-500">95%</div>
                  <div className="text-xs sm:text-sm text-gray-500">Revenue Growth (5 years)</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl sm:text-2xl font-bold text-yellow-500">25%</div>
                  <div className="text-xs sm:text-sm text-gray-500">EBITDA Margin (7 years)</div>
                </div>
              </div>
              
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
                <h4 className="font-bold text-yellow-700 mb-2 text-sm">Implementation Philosophy:</h4>
                <p className="text-gray-700 italic text-sm">
                  "You must let go of classical cost thinking and look at the end result after three years. 
                  That helped us persevere and keep investing in digitalization." - Jan Pauwels, Manufacturing Leader
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 text-sm">Quick Pilot Results (2 months):</h4>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>Delivery reliability: 80% to 99.5%</li>
                    <li>Work-in-progress: Halved</li>
                    <li>Digital transformation: PROPOS + Azumuta</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 text-sm">Long-term Success (3-7 years):</h4>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>Average cost reduction: ~25%</li>
                    <li>Implementation duration: 3-5 years typical</li>
                    <li>2025 LEAN LEAD WINNER for Best QRM Practice</li>
                  </ul>
                </div>
              </div>
            </Card>
          )}
          
          {/* Additional Resources Navigation */}
          {results && (
            <div className="mt-6 sm:mt-8 text-center">
              <Card className="p-6 bg-white border-gray-200 shadow-sm rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3 sm:mb-4">Plan Your Implementation</h3>
                <p className="text-gray-500 mb-4 text-sm">
                  Now that you know the ROI potential, understand what the implementation will actually cost
                </p>
                <Button 
                  onClick={() => window.location.href = '/implementation-costs'}
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold mr-3 rounded"
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  View Implementation Costs
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.location.href = '/recommendations'}
                  className="px-6 py-2"
                >
                  ← Back to Recommendations
                </Button>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EBITDABusinessCase;
